import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  type ElementType,
} from "react";
import useSWR from "swr";
import {
  Box,
  Container,
  Text,
  Heading,
  VStack,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Button,
  useColorMode,
  Badge,
  IconButton,
  Stack,
  Flex,
  useBreakpointValue,
  usePrefersReducedMotion,
  Collapse,
  SlideFade,
  Tooltip,
  Icon,
} from "@chakra-ui/react";
import type { StackDirection } from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  AddIcon,
  MinusIcon,
  RepeatIcon,
  CloseIcon,
  ExternalLinkIcon,
  ViewIcon,
  ViewOffIcon,
  MoonIcon,
  SunIcon,
  ArrowUpDownIcon,
  HamburgerIcon,
} from "@chakra-ui/icons";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ContentItem, Book } from "../libs/types";
import { APP_CONSTANTS } from "../constants";
import Menu from "./Menu";
import BookCard from "./BookCard";
import ReadMode from "./ReadMode";
import ChapterNavigation from "./ChapterNavigation";
import TableOfContent from "./TableOfContent";
import { smoothScrollTo } from "../utils/scrollUtils";
import { useBookStore } from "../store/bookStore";
import { fetchBookWithLanguageFallback } from "../libs/qazbilim-backend";
import { TFunction } from "i18next";
import FallbackLanguageBar from "./FallbackLanguageBar";
import BookContent from "./BookContent";
import useChapterNavigation from "../hooks/useChapterNavigation";
import LoadingLogo from "./LoadingLogo";
import BookShare from "./Share";

const ReadBook: React.FC = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { id, chapterId } = useParams<{ id: string; chapterId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isPreviewMode = id === "preview";
  const previewState = isPreviewMode
    ? (location.state as { previewBook?: Book; returnTo?: string } | null)
    : null;
  const previewBook = previewState?.previewBook ?? null;
  const previewReturnTo = previewState?.returnTo ?? null;
  const prefersReducedMotion = usePrefersReducedMotion();

  // Keep content reference for scrolling features
  const contentRef = useRef<HTMLDivElement>(null);
  const [showBookDetails, setShowBookDetails] = useState(true);
  const [isTOCOpen, setIsTOCOpen] = useState(false);

  // Font size control state
  const [fontSize, setFontSize] = useState<number>(1); // 1 is the default size multiplier

  // Font size adjustment functions
  const increaseFontSize = () => {
    if (fontSize < 1.8) {
      // Max size limit
      setFontSize((prev) => Math.min(prev + 0.1, 1.8));
    }
  };

  const decreaseFontSize = () => {
    if (fontSize > 0.8) {
      // Min size limit
      setFontSize((prev) => Math.max(prev - 0.1, 0.8));
    }
  };

  const resetFontSize = () => {
    // Set directly to 1 to avoid potential floating point issues
    setFontSize(1);
  };

  // Calculate the actual font sizes based on the multiplier
  const actualFontSize = {
    base: `${fontSize * 1}rem`, // Default: 1rem
    lg: `${fontSize * 1.125}rem`, // Default: 1.125rem
  };

  // Round to 1 decimal place for display and comparison
  const roundedFontSize = Math.round(fontSize * 10) / 10;
  const isDefaultSize = roundedFontSize === 1;

  const headingFontSize = {
    base: `${fontSize * 1.25}rem`, // Default: 1.25rem for "md" heading
  };

  // Responsive layout helpers
  const isMobile = useBreakpointValue({ base: true, md: false });
  const imageSize = useBreakpointValue({
    base: { width: "100%", height: "auto", maxHeight: "350px" },
    md: { width: "180px", height: "270px" },
    lg: { width: "200px", height: "300px" },
  }) || { width: "180px", height: "270px", maxHeight: "300px" }; // Fallback
  const headerLayout = useBreakpointValue<StackDirection>({
    base: "column",
    md: "row",
  });

  const readLanguage = useMemo(() => {
    if (i18n.language === "kk-latn" || i18n.language === "kk-Latn") {
      return "kk-latn";
    }
    return i18n.language;
  }, [i18n.language]);

  const bookSwrKey =
    !isPreviewMode && id ? (["book", id, readLanguage, "read"] as const) : null;

  const {
    data: bookFetchResult,
    error: bookFetchError,
    isLoading: bookIsLoading,
  } = useSWR(bookSwrKey, ([, bookId, lang]) =>
    fetchBookWithLanguageFallback(bookId, lang),
  );

  const { setBook, setPreviewMode, setRestoreForm, clearBook, isPreview } =
    useBookStore();

  const isLocalizationError = bookFetchResult?.isLocalizationError ?? false;
  const fallbackLanguage = bookFetchResult?.fallbackLanguage ?? null;
  const fetchErrorMessage =
    bookFetchError instanceof Error
      ? bookFetchError.message
      : bookFetchError
        ? String(bookFetchError)
        : null;
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  const pushChapterToUrl = React.useCallback(
    (index: number) => {
      if (isPreviewMode || !id) return;
      const path = `/read/${id}/${index + 1}`;
      if (location.pathname !== path) {
        navigate(path);
      }
    },
    [isPreviewMode, id, navigate, location.pathname],
  );

  const bookData = previewBook ?? bookFetchResult?.book;

  // Calculate content and pagination
  const contentItems = bookData?.content || [];
  const totalContentItems = contentItems.length;
  const currentContentItem = contentItems[currentChapterIndex];

  useEffect(() => {
    if (isPreviewMode) return;
    if (totalContentItems === 0) return;
    if (chapterId !== undefined) {
      const n = parseInt(chapterId, 10);
      if (!Number.isNaN(n)) {
        const zeroBased = Math.max(0, n - 1);
        const clamped = Math.min(zeroBased, totalContentItems - 1);
        setCurrentChapterIndex(clamped);
      }
    } else {
      setCurrentChapterIndex(0);
    }
  }, [chapterId, totalContentItems, isPreviewMode]);

  // Monitor language changes to ensure UI updates
  useEffect(() => {
    // Force component to re-render when language changes
    const langChangeHandler = () => {
      // Force a state update to trigger re-render with new translations
      setDebugInfo(null);
    };

    // Listen for language changes from i18n
    i18n.on("languageChanged", langChangeHandler);

    // Listen for custom language change event from Menu component
    const customLangChangeHandler = () => {
      // Force re-render with new translations
      setDebugInfo(null);
    };

    document.addEventListener("languageChanged", customLangChangeHandler);

    return () => {
      i18n.off("languageChanged", langChangeHandler);
      document.removeEventListener("languageChanged", customLangChangeHandler);
    };
  }, [i18n]);

  useEffect(() => {
    if (isPreviewMode && previewBook) {
      setBook(previewBook);
      setPreviewMode(true);
      return () => {
        setPreviewMode(false);
        clearBook({ preserveRestoreForm: true });
      };
    }
    if (!isPreviewMode) {
      return () => {
        setPreviewMode(false);
        useBookStore.getState().clearBook({ preserveRestoreForm: true });
      };
    }
  }, [isPreviewMode, previewBook, setBook, setPreviewMode, clearBook]);

  useEffect(() => {
    if (!bookFetchError || isPreviewMode || !id) return;
    setDebugInfo(
      `ID: ${id}, Language: ${readLanguage} (detected: ${i18n.language})`,
    );
  }, [bookFetchError, id, readLanguage, i18n.language, isPreviewMode]);

  // Use the shared navigation hook for standard chapter navigation
  const {
    isNavigating,
    handlePreviousChapter: enhancedPrevious,
    handleNextChapter: enhancedNext,
  } = useChapterNavigation({
    prefersReducedMotion,
    onPreviousChapter: () => {
      if (currentChapterIndex > 0) {
        const next = currentChapterIndex - 1;
        setCurrentChapterIndex(next);
        pushChapterToUrl(next);
      }
    },
    onNextChapter: () => {
      if (currentChapterIndex < totalContentItems - 1) {
        const next = currentChapterIndex + 1;
        setCurrentChapterIndex(next);
        pushChapterToUrl(next);
      }
    },
    contentRef,
    headerOffset: 120, // Larger offset for ReadBook to account for navigation elements
    watchIndex: currentChapterIndex, // Watch for external chapter changes
  });

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        enhancedPrevious();
      } else if (
        e.key === "ArrowRight" ||
        e.key === "ArrowDown" ||
        e.key === " "
      ) {
        enhancedNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentChapterIndex, totalContentItems, enhancedPrevious, enhancedNext]);

  const goBack = () => {
    navigate("/");
  };

  const [isFullPageMode, setIsFullPageMode] = useState(false);

  // Toggle full page reading mode
  const toggleFullPageMode = () => {
    // Simplified transition without animations
    if (!isFullPageMode) {
      setIsFullPageMode(true);
      setShowBookDetails(false);
    } else {
      setIsFullPageMode(false);
      setShowBookDetails(true);
    }
  };

  // Add helper function for language names
  const getLanguageDisplayName = (
    code: string,
    displayLanguage: string,
  ): string => {
    // Normalize language codes to handle case sensitivity
    const normalizedCode = code === "kk-Latn" ? "kk-latn" : code;

    // Handle unknown or missing language codes
    if (!normalizedCode || normalizedCode === "unknown") {
      const unknownTranslations: Record<string, string> = {
        en: "the original language",
        ru: "оригинальном языке",
        kk: "түпнұсқа тілінде",
        "kk-latn": "tüpnusqa tilinde",
      };

      // Safely check if the display language has a translation
      return displayLanguage in unknownTranslations
        ? unknownTranslations[
            displayLanguage as keyof typeof unknownTranslations
          ]
        : "the original language";
    }

    try {
      // Use the Intl.DisplayNames API if available
      if (typeof Intl !== "undefined" && Intl.DisplayNames) {
        const displayName = new Intl.DisplayNames([displayLanguage], {
          type: "language",
        }).of(normalizedCode);
        return displayName || normalizedCode;
      }
    } catch (e) {
      console.warn("Error getting display name for language", e);
    }

    // Fallback for language display names
    const languageNames: Record<string, Record<string, string>> = {
      en: {
        en: "English",
        ru: "Russian",
        kk: "Qazaq (Cyrillic)",
        "kk-latn": "Qazaq (Latin)",
      },
      ru: {
        en: "Английский",
        ru: "Русский",
        kk: "Казахский (Кириллица)",
        "kk-latn": "Казахский (Латиница)",
      },
      kk: {
        en: "Ағылшын",
        ru: "Орыс",
        kk: "Қазақ (Кириллица)",
        "kk-latn": "Қазақ (Латиница)",
      },
      "kk-latn": {
        en: "Ağılşın",
        ru: "Orıs",
        kk: "Qazaq (Kïrïllïca)",
        "kk-latn": "Qazaq (Latïnïca)",
      },
    };

    // Handle the case where we have kk-Latn instead of kk-latn
    const normalizedDisplayLanguage =
      displayLanguage === "kk-Latn" ? "kk-latn" : displayLanguage;

    return (
      (languageNames[normalizedDisplayLanguage] &&
        languageNames[normalizedDisplayLanguage][normalizedCode]) ||
      normalizedCode
    );
  };

  // Use the ReadMode component for full page reading
  if (isFullPageMode && currentContentItem) {
    return (
      <>
        <TableOfContent
          isOpen={isTOCOpen}
          onClose={() => setIsTOCOpen(false)}
          contentItems={contentItems}
          currentChapterIndex={currentChapterIndex}
          onChapterSelect={(index) => {
            if (index !== currentChapterIndex) {
              setCurrentChapterIndex(index);
              pushChapterToUrl(index);
            }
          }}
          isMobile={isMobile}
          t={t}
        />
        <ReadMode
          currentChapterIndex={currentChapterIndex}
          totalContentItems={totalContentItems}
          currentContentItem={currentContentItem}
          contentItems={contentItems}
          handlePreviousChapter={enhancedPrevious}
          handleNextChapter={enhancedNext}
          exitFullPageMode={toggleFullPageMode}
          increaseFontSize={increaseFontSize}
          decreaseFontSize={decreaseFontSize}
          resetFontSize={resetFontSize}
          fontSize={fontSize}
          roundedFontSize={roundedFontSize}
          isDefaultSize={isDefaultSize}
          actualFontSize={actualFontSize}
          headingFontSize={headingFontSize}
          t={t}
          i18n={i18n}
          prefersReducedMotion={prefersReducedMotion}
          isMobile={isMobile}
          language={i18n.language}
          onOpenTableOfContents={() => setIsTOCOpen(true)}
          fallbackLanguage={fallbackLanguage}
          getLanguageDisplayName={getLanguageDisplayName}
        />
      </>
    );
  }

  // Add loading state handling (skip when preview)
  if (!isPreviewMode && bookIsLoading) {
    return (
      <>
        <Menu />
        <Box id="read-book-loading-container" pt={4}>
          <Center minH="400px">
            <VStack spacing={4}>
              <LoadingLogo size="xl" />
              <Text id="read-book-loading-text">{t("readBook.loading")}</Text>
            </VStack>
          </Center>
        </Box>
      </>
    );
  }

  // Add error handling (skip when preview - we have previewBook)
  if (!isPreviewMode && !bookIsLoading && (fetchErrorMessage || !bookData)) {
    return (
      <>
        <Menu />
        <Box
          id="read-book-error-container"
          pt={4}
          px={{ base: 0, md: 0 }}
          className="read-book-error-container"
        >
          <Container
            id="read-book-error-inner-container"
            maxW={{
              base: "container.md",
              lg: "container.lg",
              xl: "container.xl",
            }}
            px={{ base: 2, md: 2 }}
            className="read-book-error-inner-container"
          >
            <Box
              id="read-book-error-content-wrapper"
              sx={{
                // Apply identical responsive settings as the main navigation Flex
                maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
                mx: "auto",
              }}
              className="read-book-error-content-wrapper"
            >
              <Alert
                id="read-book-error-alert"
                status="warning"
                mb={6}
                borderRadius="md"
                bg={colorMode === "light" ? undefined : "rgba(45, 55, 72, 0.8)"}
                color={colorMode === "light" ? undefined : "white"}
                className="read-book-error-alert"
              >
                <AlertIcon />
                <VStack
                  id="read-book-error-text-wrapper"
                  align="start"
                  spacing={2}
                  width="100%"
                  className="read-book-error-text-wrapper"
                >
                  <Text
                    id="read-book-error-heading"
                    fontWeight="bold"
                    className="read-book-error-heading"
                  >
                    {isLocalizationError
                      ? t(
                          "readBook.notLocalized",
                          "This book is not available in your selected language yet",
                        )
                      : fetchErrorMessage || t("readBook.notFound")}
                  </Text>
                  <Text
                    id="read-book-error-description"
                    fontSize="sm"
                    className="read-book-error-description"
                  >
                    {isLocalizationError
                      ? t(
                          "readBook.tryDifferentLanguage",
                          "Please try selecting a different language or check back later for translations.",
                        )
                      : t(
                          "readBook.bookLoadError",
                          "The requested book could not be found or loaded. Please try again later.",
                        )}
                  </Text>
                  {debugInfo && !isLocalizationError && (
                    <Text
                      id="read-book-error-debug"
                      fontSize="xs"
                      color={colorMode === "light" ? "gray.500" : "gray.400"}
                      className="read-book-error-debug"
                    >
                      Debug info: {debugInfo}
                    </Text>
                  )}
                </VStack>
              </Alert>
              <Button
                id="read-book-error-back-button"
                leftIcon={<ArrowBackIcon />}
                onClick={goBack}
                variant="outline"
                w={{ base: "full", md: "auto" }}
                className="read-book-back-button"
              >
                {t("readBook.goBack")}
              </Button>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  // Preview mode without state: go back in history or fallback to create
  if (isPreviewMode && !previewBook) {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/create");
    }
    return null;
  }

  // When not in preview, require bookData
  if (!isPreview && !bookData) {
    return null;
  }

  // In preview mode without bookData: show minimal fallback and allow close
  if (isPreview && !bookData) {
    return (
      <Box p={4}>
        <Button
          leftIcon={<CloseIcon />}
          onClick={() =>
            window.history.length > 1 ? navigate(-1) : navigate("/create")
          }
          variant="outline"
        >
          {t("createBook.preview.closePreview")}
        </Button>
        <Text mt={4}>{t("createBook.preview.untitled")}</Text>
      </Box>
    );
  }

  // At this point bookData is defined (TypeScript narrowing)
  if (!bookData) return null;

  return (
    <>
      <TableOfContent
        isOpen={isTOCOpen}
        onClose={() => setIsTOCOpen(false)}
        contentItems={contentItems}
        currentChapterIndex={currentChapterIndex}
        onChapterSelect={(index) => {
          if (index !== currentChapterIndex) {
            setCurrentChapterIndex(index);
            pushChapterToUrl(index);
          }
        }}
        isMobile={isMobile}
        t={t}
      />
      <Menu />
      <Box
        id="read-book-container"
        pt={fallbackLanguage ? 8 : 4}
        px={{ base: 0, md: 0 }}
      >
        <Container
          id="read-book-inner-container"
          maxW={{
            base: "container.md",
            lg: "container.lg",
            xl: "container.xl",
          }}
          px={{ base: 2, md: 2 }}
        >
          <FallbackLanguageBar
            fallbackLanguage={fallbackLanguage}
            getLanguageDisplayName={getLanguageDisplayName}
            t={t}
            i18n={i18n}
            isFixed={false}
          />

          <Flex
            id="read-book-navigation"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
            direction={{ base: "column", sm: "row" }}
            gap={2}
            width="100%"
            sx={{
              maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
              mx: "auto",
            }}
          >
            <Button
              id="read-book-back-button"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                if (isPreviewMode && bookData) {
                  setRestoreForm(bookData);
                  if (previewReturnTo) {
                    navigate(previewReturnTo, {
                      state: { restoreForm: bookData },
                    });
                    return;
                  }
                }
                if (isPreviewMode) {
                  navigate(-1);
                } else {
                  navigate("/");
                }
              }}
              variant="outline"
              size="md"
              width={{ base: "100%", sm: "auto" }}
            >
              {isPreviewMode
                ? t("createBook.preview.closePreview", "Close preview")
                : t("readBook.goBack")}
            </Button>

            <Button
              id="read-book-toc-button"
              leftIcon={<HamburgerIcon />}
              onClick={() => setIsTOCOpen(true)}
              variant="outline"
              size="md"
              width={{ base: "100%", sm: "auto" }}
            >
              {t("readBook.tableOfContents", "Table of Contents")}
            </Button>
          </Flex>

          {/* Book header - Only shown when not in full-page mode and details are expanded */}
          <Box
            id="read-book-header-container"
            maxHeight={showBookDetails ? "1000px" : "0"}
            opacity={showBookDetails ? 1 : 0}
            overflow="hidden"
            transition="all 0.9s cubic-bezier(0.25, 0.1, 0.25, 1)"
            mb={showBookDetails ? 6 : 0}
            transform={showBookDetails ? "translateY(0)" : "translateY(-20px)"}
            sx={{
              maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
              mx: "auto",
            }}
          >
            <Box
              id="read-book-header-content"
              p={{ base: 4, md: 4 }}
              borderRadius="md"
              bg={
                colorMode === "light"
                  ? "rgba(240, 240, 240, 0.6)"
                  : "rgba(30, 35, 45, 0.6)"
              }
              boxShadow="sm"
            >
              <Stack
                id="read-book-header-stack"
                direction={headerLayout}
                spacing={{ base: 4, md: 8 }}
                align={{ base: "center", md: "flex-start" }}
              >
                <Box
                  id="read-book-cover-container"
                  width={imageSize.width}
                  textAlign={{ base: "center", md: "left" }}
                  position="relative"
                  flexShrink={0}
                >
                  <BookCard
                    title={bookData.title}
                    coverUrl={bookData.coverUrl}
                    width={imageSize.width}
                    height={imageSize.height}
                    maxHeight={imageSize.maxHeight}
                    boxShadow="sm"
                  />
                </Box>
                <VStack
                  id="read-book-info-stack"
                  align="flex-start"
                  spacing={4}
                  width="100%"
                >
                  <VStack
                    id="read-book-title-stack"
                    align={{ base: "center", md: "flex-start" }}
                    spacing={2}
                    width="100%"
                  >
                    <Heading
                      id="read-book-title"
                      size="lg"
                      textAlign={{ base: "center", md: "left" }}
                      width="100%"
                      color={colorMode === "light" ? "gray.800" : "white"}
                    >
                      {bookData.title}
                    </Heading>
                    <Text
                      id="read-book-author"
                      fontSize="lg"
                      textAlign={{ base: "center", md: "left" }}
                      width="100%"
                      color={colorMode === "light" ? "gray.700" : "gray.200"}
                    >
                      {bookData.author}
                    </Text>
                    <Text
                      id="read-book-summary"
                      mt={2}
                      textAlign={{ base: "center", md: "left" }}
                      width="100%"
                      noOfLines={3}
                      color={colorMode === "light" ? "gray.600" : "gray.300"}
                    >
                      {bookData.summary}
                    </Text>
                  </VStack>
                </VStack>
              </Stack>
            </Box>
          </Box>

          {/* Font Size Controls and Full-page Mode Toggle */}
          <Box
            id="read-book-controls-container"
            sx={{
              maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
              mx: "auto",
            }}
          >
            {/* Fullscreen button - Mobile only - Full width button */}
            <Box
              id="read-book-fullscreen-mobile"
              width="100%"
              mb={4}
              display={{ base: "block", md: "none" }}
            >
              <Button
                id="read-book-fullscreen-mobile-button"
                leftIcon={<ArrowUpDownIcon />}
                onClick={toggleFullPageMode}
                width="100%"
                colorScheme="blackAlpha"
                bg={colorMode === "light" ? "gray.800" : "gray.900"}
                color="white"
                _hover={{
                  bg: colorMode === "light" ? "black" : "gray.700",
                }}
                variant="solid"
                boxShadow="md"
                py={6}
              >
                {t("readBook.fullPageMode", "Full-page mode")}
              </Button>
            </Box>

            {/* Font controls - Wrap on small screens */}
            <Flex
              id="read-book-controls"
              justify="flex-end"
              wrap="wrap"
              gap={2}
              mb={4}
            >
              {/* Color mode toggle */}
              <IconButton
                id="read-book-color-mode-toggle"
                aria-label={
                  colorMode === "light"
                    ? t("readBook.darkMode", "Switch to dark mode")
                    : t("readBook.lightMode", "Switch to light mode")
                }
                icon={
                  colorMode === "light" ? (
                    <MoonIcon boxSize={4} />
                  ) : (
                    <SunIcon boxSize={4} />
                  )
                }
                size="md"
                isRound
                variant="outline"
                onClick={toggleColorMode}
              />

              {/* Font size controls */}
              <IconButton
                id="read-book-decrease-font-button"
                aria-label={t(
                  "readBook.decreaseFontSize",
                  "Decrease font size",
                )}
                icon={<MinusIcon boxSize={5} />}
                size="md"
                isRound
                variant="outline"
                onClick={decreaseFontSize}
                isDisabled={roundedFontSize <= 0.8}
              />

              <Text
                id="read-book-font-size-display"
                fontWeight="medium"
                minWidth="50px"
                textAlign="center"
                color={colorMode === "light" ? "gray.700" : "gray.200"}
                alignSelf="center"
              >
                {Math.round(fontSize * 100)}%
              </Text>

              <IconButton
                id="read-book-increase-font-button"
                aria-label={t(
                  "readBook.increaseFontSize",
                  "Increase font size",
                )}
                icon={<AddIcon boxSize={5} />}
                size="md"
                isRound
                variant="outline"
                onClick={increaseFontSize}
                isDisabled={roundedFontSize >= 1.8}
              />

              <IconButton
                id="read-book-reset-font-button"
                aria-label={t("readBook.resetFontSize", "Reset font size")}
                icon={<RepeatIcon boxSize={4} />}
                size="md"
                isRound
                variant="outline"
                onClick={resetFontSize}
                isDisabled={isDefaultSize}
              />

              {!isPreviewMode && id && (
                <BookShare
                  book={{
                    title: bookData.title,
                    coverUrl: bookData.coverUrl,
                  }}
                  bookId={id}
                  chapterOneBased={currentChapterIndex + 1}
                />
              )}

              {/* Add Table of Contents button */}
              <IconButton
                id="read-book-toc-icon-button"
                aria-label={t("readBook.tableOfContents", "Table of Contents")}
                icon={<HamburgerIcon boxSize={4} />}
                size="md"
                isRound
                variant="outline"
                onClick={() => setIsTOCOpen(true)}
              />

              {/* Fullscreen button - Desktop only */}
              <IconButton
                id="read-book-fullscreen-desktop-button"
                aria-label={t("readBook.fullPageMode", "Full-page mode")}
                icon={<ArrowUpDownIcon boxSize={4} />}
                size="md"
                isRound
                bg={colorMode === "light" ? "gray.800" : "gray.700"}
                color="white"
                _hover={{
                  bg: colorMode === "light" ? "black" : "gray.900",
                }}
                onClick={toggleFullPageMode}
                display={{ base: "none", md: "flex" }}
              />
            </Flex>
          </Box>

          {/* Content Display with integrated navigation */}
          <Box
            position="relative"
            sx={{
              touchAction: "auto", // Allow normal touch behavior
            }}
            id="book-content-wrapper"
          >
            {currentContentItem && (
              <BookContent
                ref={contentRef}
                chapter={currentContentItem.chapter}
                text={currentContentItem.text}
                prefersReducedMotion={prefersReducedMotion}
                isMobile={isMobile}
                t={t}
                fontSize={actualFontSize}
                headingFontSize={headingFontSize}
                currentChapterIndex={currentChapterIndex}
                totalContentItems={totalContentItems}
                handlePreviousChapter={enhancedPrevious}
                handleNextChapter={enhancedNext}
                onOpenTableOfContents={() => setIsTOCOpen(true)}
                showNavigation={totalContentItems > 0}
                isNavigating={isNavigating}
                navigationVariant={isFullPageMode ? "fullscreen" : "default"}
                language={i18n.language}
              />
            )}

            {!currentContentItem && (
              <Alert
                id="read-book-no-content-alert"
                status="warning"
                borderRadius="md"
                bg={colorMode === "light" ? undefined : "rgba(45, 55, 72, 0.8)"}
                color={colorMode === "light" ? undefined : "white"}
              >
                <AlertIcon />
                <Text id="read-book-no-content-text">
                  {t("readBook.noContent", "No content available")}
                </Text>
              </Alert>
            )}
          </Box>

          {/* Note: Bottom navigation is now integrated into BookContent */}
        </Container>
      </Box>
    </>
  );
};

export default ReadBook;
